import { z } from "zod";
import { appConfigSchema } from "./appConfig";
import { issueSchema } from "./common";

// "empty" — псевдостатус ответа `/config/published`, когда ещё ничего не публиковали (контракт §Конфигурация).
export const revisionStatusSchema = z.enum(["published", "superseded", "rolled_back", "empty"]);
export type RevisionStatus = z.infer<typeof revisionStatusSchema>;

export const revisionSchema = z.object({
  // id — первичный ключ БД (целое), number — номер ревизии; это разные числа, в URL всегда number.
  id: z.number().int(),
  // По снимку (`RevisionOut.number`) поле nullable: клиентская схема была строже сервера (P3-11).
  number: z.number().int().min(0).nullable(),
  status: revisionStatusSchema,
  note: z.string().nullable(),
  schema_version: z.number().int(),
  document_hash: z.string(),
  created_by_email: z.string().nullable(),
  created_at: z.string().nullable(),
  published_at: z.string().nullable(),
  published_by_email: z.string().nullable(),
  parent_revision_number: z.number().int().nullable(),
});
export type Revision = z.infer<typeof revisionSchema>;

export const publishedResponseSchema = z.object({
  revision: revisionSchema,
  document: appConfigSchema,
});
export type PublishedResponse = z.infer<typeof publishedResponseSchema>;

export const revisionsListResponseSchema = z.object({
  items: z.array(revisionSchema),
  next_cursor: z.string().nullable().optional(),
});
export type RevisionsListResponse = z.infer<typeof revisionsListResponseSchema>;

export const revisionWithDocumentSchema = z.object({
  revision: revisionSchema,
  document: appConfigSchema,
});
export type RevisionWithDocument = z.infer<typeof revisionWithDocumentSchema>;

export const jsonPatchOpSchema = z.object({
  op: z.enum(["add", "remove", "replace", "move", "copy", "test"]),
  path: z.string(),
  value: z.unknown().optional(),
  from: z.string().optional(),
});
export type JsonPatchOp = z.infer<typeof jsonPatchOpSchema>;

export const diffResponseSchema = z.object({
  patch: z.array(jsonPatchOpSchema),
  summary: z.array(z.string()),
});
export type DiffResponse = z.infer<typeof diffResponseSchema>;

export const rollbackResponseSchema = z.object({ revision: revisionSchema });
export const publishResponseSchema = z.object({ revision: revisionSchema });

export const draftSchema = z.object({
  id: z.number().int(),
  base_published_number: z.number().int().min(0),
  stale: z.boolean(),
  created_by_email: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  note: z.string().nullable(),
});
export type Draft = z.infer<typeof draftSchema>;

export const draftsListResponseSchema = z.object({ items: z.array(draftSchema) });
export type DraftsListResponse = z.infer<typeof draftsListResponseSchema>;

export const draftResponseSchema = z.object({ draft: draftSchema, document: appConfigSchema });
export type DraftResponse = z.infer<typeof draftResponseSchema>;

export const draftMutationResponseSchema = z.object({ draft: draftSchema });
export type DraftMutationResponse = z.infer<typeof draftMutationResponseSchema>;

export const validateResponseSchema = z.object({
  errors: z.array(issueSchema),
  warnings: z.array(issueSchema),
});
export type ValidateResponse = z.infer<typeof validateResponseSchema>;

export const previewAssignmentSchema = z.object({
  surface: z.string(),
  group_parent: z.string().nullable().optional(),
  variant: z.string(),
  bucket: z.number().int(),
  resolved: z.boolean(),
  template: z.string().nullable().optional(),
});
export type PreviewAssignment = z.infer<typeof previewAssignmentSchema>;

export const previewResponseSchema = z.object({
  install_key: z.string(),
  assignments: z.array(previewAssignmentSchema),
  flags: z.record(z.string(), z.unknown()),
});
export type PreviewResponse = z.infer<typeof previewResponseSchema>;

export const templateEntrySchema = z.object({
  title: z.string(),
  description: z.string(),
  min_app_version: z.string(),
  retired_at: z.string().nullable(),
  params_schema: z.unknown(),
});

/**
 * Реестр шаблонов из БД бэкенда: `params_schema` приходит самодостаточной (свой `$defs` внутри),
 * верхнеуровневых `description`/`$defs` из файла контрактов в ответе нет (контракт §Конфигурация).
 */
export const templatesResponseSchema = z.object({
  schema_version: z.number().int(),
  capabilities: z.array(z.string()),
  paywall: z.record(z.string(), templateEntrySchema),
  onboarding_step: z.record(z.string(), templateEntrySchema),
  seeded_at: z.string().nullable(),
});
export type TemplatesResponse = z.infer<typeof templatesResponseSchema>;

/** Экспозиции вариантов за два окна: `recent` — колонка в списках, `guard` — запрет удаления. */
export const exposureCountSchema = z.object({
  surface: z.string(),
  variant: z.string(),
  recent: z.number().int().nonnegative(),
  guard: z.number().int().nonnegative(),
  last_shown_at: z.string(),
});
export type ExposureCount = z.infer<typeof exposureCountSchema>;

export const exposuresResponseSchema = z.object({
  windows: z.object({
    recent_days: z.number().int().positive(),
    guard_days: z.number().int().positive(),
  }),
  generated_at: z.string(),
  items: z.array(exposureCountSchema),
});
export type ExposuresResponse = z.infer<typeof exposuresResponseSchema>;
